import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Table from '../../components/Table/Table';
import CarForm, { PendingCarDocument } from '../../components/HR/CarForm';
import EntityDocumentsModal from '../../components/HR/EntityDocumentsModal';
import { carDocumentsService, carsService } from '../../services';
import { Car, TableColumn } from '../../types';
import { useBranches } from '../../hooks/useBranches';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { useCars, useCreateCar, useUpdateCar, useDeleteCar } from '../../hooks/useCars';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState, Pagination } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';

const isoDatePlusDays = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const Cars: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCar, setEditingCar] = useState<Car | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [expiryDateFilter, setExpiryDateFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [documentsCar, setDocumentsCar] = useState<Car | undefined>();

  const debouncedSearch = useDebouncedValue(searchTerm);

  const fetchCarDocuments = useCallback(async () => {
    if (!documentsCar) return [];
    const response: any = await carDocumentsService.getAll({
      filters: { car: { documentId: { $eq: documentsCar.documentId } } },
    });
    return response.data || [];
  }, [documentsCar]);

  // All filters run server-side so search and pagination cover the whole dataset
  const serverFilters = useMemo(() => {
    const conditions: any[] = [];
    if (statusFilter !== 'all') conditions.push({ car_status: { $eq: statusFilter } });
    if (branchFilter !== 'all') conditions.push({ branch: { id: { $eq: branchFilter } } });
    if (yearFilter !== 'all') conditions.push({ manufacturingYear: { $eq: yearFilter } });
    if (expiryDateFilter === 'expired') {
      conditions.push({ licenseExpiry: { $lt: isoDatePlusDays(0) } });
    } else if (expiryDateFilter === 'expiring_soon') {
      conditions.push({ licenseExpiry: { $gte: isoDatePlusDays(0), $lte: isoDatePlusDays(30) } });
    } else if (expiryDateFilter === 'valid') {
      conditions.push({ $or: [{ licenseExpiry: { $gt: isoDatePlusDays(30) } }, { licenseExpiry: { $null: true } }] });
    }
    if (dateFromFilter) conditions.push({ licenseExpiry: { $gte: dateFromFilter } });
    if (dateToFilter) conditions.push({ licenseExpiry: { $lte: dateToFilter } });
    return conditions.length ? { $and: conditions } : undefined;
  }, [statusFilter, branchFilter, yearFilter, expiryDateFilter, dateFromFilter, dateToFilter]);

  const filtersKey = JSON.stringify(serverFilters || {}) + debouncedSearch;
  useEffect(() => { setPage(1); }, [filtersKey]);

  const queryOptions = useMemo(() => ({
    pagination: { page, pageSize },
    sort: [{ field: 'code', order: 'asc' as const }],
    ...(serverFilters ? { filters: serverFilters } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
  }), [page, pageSize, serverFilters, debouncedSearch]);

  // Branches have no `code` field — sorting them by code makes Strapi return 400.
  const branchQueryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'name', order: 'asc' as const }]
  }), []);

  const { data: cars, meta, isLoading, error, refetch } = useCars(queryOptions);
  const { data: branches = [] } = useBranches(branchQueryOptions);
  const totalCount = meta?.pagination?.total ?? cars?.length ?? 0;

  const createMutation = useCreateCar(() => refetch());
  const updateMutation = useUpdateCar(() => refetch());
  const deleteMutation = useDeleteCar(() => refetch());

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const uniqueYears = useMemo(() => {
    if (!cars || cars.length === 0) return [];
    return Array.from(new Set(cars.map(car => car.manufacturingYear).filter(Boolean)));
  }, [cars]);

  // Exports cover the entire filtered dataset, not just the visible page
  const fetchAllFiltered = useCallback(async (): Promise<Car[]> => {
    const response: any = await carsService.getAll({
      sort: [{ field: 'code', order: 'asc' as const }],
      ...(serverFilters ? { filters: serverFilters } : {}),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
    });
    return response.data || [];
  }, [serverFilters, debouncedSearch]);

  const handleExport = async () => {
    const all = await fetchAllFiltered();
    const exportData = all.map(car => ({
      Code: car.code,
      'Owner Name': car.ownerName,
      'Plate Number': car.plateNumber,
      'Chassis Number': car.chassisNumber,
      'Engine Number': car.engineNumber,
      'Manufacturing Year': car.manufacturingYear,
      'License Expiry': car.licenseExpiry,
      Status: car.status,
      'Vessel/Headquarters': getBranchName(car.branchId),
      'Document Count': car.documents?.length || 0,
      'Created At': car.createdAt,
      'Updated At': car.updatedAt
    }));
    exportToExcel(exportData, `cars_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = async () => {
    const all = await fetchAllFiltered();
    const exportData = all.map(car => ({
      Code: car.code,
      'Owner Name': car.ownerName,
      'Plate Number': car.plateNumber,
      'Chassis Number': car.chassisNumber,
      'Engine Number': car.engineNumber,
      'Manufacturing Year': car.manufacturingYear,
      'License Expiry': car.licenseExpiry,
      Status: car.status,
      'Vessel/Headquarters': getBranchName(car.branchId),
      'Document Count': car.documents?.length || 0,
      'Created At': car.createdAt,
      'Updated At': car.updatedAt
    }));
    printAsExcel(exportData, 'Cars');
  };

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'maintenance', label: 'Maintenance' }
    ]},
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [
      { value: 'all', label: 'All Vessels/Headquarters' }, ...branches.map(b => ({ value: b.id, label: b.name }))
    ]},
    { key: 'year', label: 'Manufacturing Year', type: 'select', value: yearFilter, onChange: setYearFilter, options: [
      { value: 'all', label: 'All Years' }, ...uniqueYears.map(y => ({ value: y, label: y }))
    ]},
    { key: 'expiry', label: 'License Status', type: 'select', value: expiryDateFilter, onChange: setExpiryDateFilter, options: [
      { value: 'all', label: 'All' }, { value: 'valid', label: 'Valid' }, { value: 'expiring_soon', label: 'Expiring Soon' }, { value: 'expired', label: 'Expired' }
    ]},
    { key: 'dateFrom', label: 'Expiry From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Expiry To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const columns: TableColumn[] = [
    { key: 'code', label: 'الكود', sortable: true },
    { key: 'ownerName', label: 'اسم المالك', sortable: true },
    { key: 'plateNumber', label: 'رقم السيارة', sortable: true },
    { key: 'chassisNumber', label: 'رقم الشاسيه', sortable: true },
    { key: 'engineNumber', label: 'رقم الموتور', sortable: true },
    { key: 'manufacturingYear', label: 'سنة الصنع', sortable: true },
    { key: 'licenseExpiry', label: 'تاريخ انتهاء الرخصة', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString('ar-EG') : '-' },
    { key: 'documents', label: 'المستندات', sortable: false, render: (_: unknown, car: Car) => (
      <button
        type="button"
        className="text-ocean-600 hover:text-ocean-800 underline"
        onClick={() => setDocumentsCar(car)}
      >
        عرض
      </button>
    ) }
  ];

  const handleEdit = (car: Car) => {
    setEditingCar(car);
    setShowForm(true);
  };

  const handleDelete = async (car: Car) => {
    if (window.confirm(`Are you sure you want to delete car ${car.plateNumber}?`)) {
      await deleteMutation.execute(car);
    }
  };

  const handleAdd = () => {
    setEditingCar(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Car>, pendingDocument?: PendingCarDocument) => {
    let savedCarDocumentId: string | undefined = editingCar?.documentId;
    if (editingCar) {
      const updateData = { ...data, documentId: editingCar.documentId };
      await updateMutation.execute({ id: editingCar.id, data: updateData });
    } else {
      const created: any = await createMutation.execute(data);
      await bumpCodePrefixSequence(data.code);
      savedCarDocumentId = created?.documentId || created?.data?.documentId;
    }
    if (pendingDocument && savedCarDocumentId) {
      await carDocumentsService.create({
        car: savedCarDocumentId,
        documentType: pendingDocument.documentType,
        documentNumber: pendingDocument.documentNumber,
        expiryDate: pendingDocument.expiryDate || null,
        status: 'valid',
        filePath: pendingDocument.filePath,
      });
    }
    setShowForm(false);
    setEditingCar(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCar(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setYearFilter('all');
    setExpiryDateFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <CarForm car={editingCar} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  if (isLoading && !meta) return <TableSkeleton />;
  if (error) return <ErrorState message={error.message || String(error)} onRetry={() => refetch()} entityName="cars" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cars Management"
        description="Manage company cars, their documents, and permits"
      />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search cars..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={totalCount}
        filteredCount={totalCount}
        entityName="cars"
        onAdd={handleAdd}
        addLabel="Add Car"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <Table
            columns={columns}
            data={cars || []}
            onEdit={handleEdit}
            onDelete={handleDelete}
            title="Cars Directory"
            showAddButton={false}
            searchable={false}
          />
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          isLoading={isLoading}
        />
      </div>
      {documentsCar && (
        <EntityDocumentsModal
          title={`مستندات السيارة ${documentsCar.plateNumber}`}
          fetchDocuments={fetchCarDocuments}
          onClose={() => setDocumentsCar(undefined)}
        />
      )}
    </div>
  );
};

export default Cars;
